import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Section } from '@vnetwork/ui/layout';
import { Tabs, TabsList, TabsTrigger } from '@vnetwork/ui/primitives';
import { useAffiliateProfile } from '@/api/affiliate';
import { brand } from '@/config/brand';
import { AffiliateDashboard } from './AffiliateDashboard';
import { AffiliateAnalytics } from './AffiliateAnalytics';
import { AffiliatePayouts } from './AffiliatePayouts';
import { AffiliatePolicy } from './AffiliatePolicy';
import { AffiliateSettings } from './AffiliateSettings';
import styles from './Affiliate.module.css';

const BASE = '/account/affiliate';
const TABS = [
  { value: 'dashboard', label: 'Dashboard', slug: '' },
  { value: 'analytics', label: 'Analytics', slug: 'analytics' },
  { value: 'payouts', label: 'Payouts', slug: 'payouts' },
  { value: 'policy', label: 'Policy', slug: 'policy' },
  { value: 'settings', label: 'Settings', slug: 'settings' },
];

export function AffiliatePage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: profile, isLoading } = useAffiliateProfile();

  const seg = pathname.replace(`${BASE}`, '').replace(/^\//, '').split('/')[0] || 'dashboard';

  // Sub-pages assume an affiliate profile exists; a non-member deep-linking to
  // them would otherwise fire unauthorized queries. Send them back to the base
  // (Dashboard) which shows the join form. Wait for the profile fetch first.
  const memberOnly = (el: React.ReactNode) =>
    !isLoading && !profile ? <Navigate to={BASE} replace /> : el;

  return (
    <Section
      title="Affiliate"
      description={`Refer customers to ${brand.shortName} and earn commission.`}
    >
      {/* Tabs only make sense once the user has joined the program. */}
      {profile && (
        <Tabs
          className={styles.tabs}
          value={seg}
          onValueChange={(v) => {
            const slug = TABS.find((t) => t.value === v)?.slug ?? '';
            navigate(slug ? `${BASE}/${slug}` : BASE);
          }}
        >
          <TabsList>
            {TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      <Routes>
        <Route index element={<AffiliateDashboard />} />
        <Route path="analytics" element={memberOnly(<AffiliateAnalytics />)} />
        <Route path="payouts" element={memberOnly(<AffiliatePayouts />)} />
        <Route path="policy" element={memberOnly(<AffiliatePolicy />)} />
        <Route path="settings" element={memberOnly(<AffiliateSettings />)} />
      </Routes>
    </Section>
  );
}
